import { inject, Injectable } from '@angular/core';
import { QuoteInputModel, QuoteInputOptions } from '@lx/quote-tool/shared/types';
import { LoggerService } from '@lx/shared/logger';
import { convertTimeWIthOffsetToString, getDateStringFromDate, getToday } from '@lx/shared/utils';
import { QuoteToolLiteState } from '../store';
import { BaseAccommodation, ExtrasSummary } from '../types';
import { Timezone } from '@lx/quote-tool/shared/types';
import { ClassTime } from '@lx/quote-tool/lite/online/types';

@Injectable({
  providedIn: 'root',
})
export class QuoteInputMapper {
  private readonly loggerService = inject(LoggerService);

  /**
   * Maps properties from quote tool lite state to a quote input model.
   * If extrasSummary is provided, it will be used as fallback value for filling accommodation in case
   * there is no data for the accommodation.
   */
  mapQuoteInput(state: Partial<QuoteToolLiteState>, extrasSummary?: ExtrasSummary): QuoteInputModel {
    if (!state || Object.keys(state).length === 0) {
      this.loggerService.error('Quote input state is empty - cannot map it to quote input model');
      return;
    }

    const {
      selectedAccommodation,
      selectedResidence,
      selectedSchoolLocation,
      selectedCourse,
      selectedDuration,
      selectedStartDate,
      selectedInsuranceCode,
      selectedTransferRows,
      selectedProfessionalImmersionExperienceCode,
      quoteId,
      selectedPremiumAccommodationCode,
      selectedPremiumActivities,
      selectedPrivateLesson,
      selectedUnaccompaniedMinorRows,
    } = state;

    const options = {
      residenceCountry: selectedResidence?.countryCode,
      schoolCode: selectedSchoolLocation?.schoolCode,
      quoteDate: getDateStringFromDate(getToday()),
      tuitionStartDate: selectedStartDate,
      productPackage: selectedCourse?.productPackage,
    } as QuoteInputOptions;

    const products = [];

    if (selectedCourse?.code) {
      products.push({
        code: selectedCourse?.code,
        units: selectedDuration,
      });
    }

    const accommodationProduct = this.getAccommodationProduct(selectedAccommodation, selectedDuration, extrasSummary);
    if (accommodationProduct) {
      products.push(accommodationProduct);
    }

    if (selectedInsuranceCode) {
      products.push({
        code: selectedInsuranceCode,
        units: selectedDuration,
      });
    }

    if (selectedProfessionalImmersionExperienceCode) {
      products.push({
        code: selectedProfessionalImmersionExperienceCode,
        units: selectedDuration,
      });
    }

    if (selectedPremiumAccommodationCode) {
      products.push({
        code: selectedPremiumAccommodationCode,
        units: selectedDuration,
      });
    }

    if (selectedPremiumActivities?.length) {
      products.push(
        ...selectedPremiumActivities.map(({ code, units }) => ({
          code,
          units,
        })),
      );
    }

    if (selectedTransferRows?.length) {
      products.push(
        ...selectedTransferRows.map((row) => ({
          row,
        })),
      );
    }

    if (selectedUnaccompaniedMinorRows?.length) {
      products.push(
        ...selectedUnaccompaniedMinorRows.map((row) => ({
          row,
        })),
      );
    }

    if (selectedPrivateLesson?.code && selectedPrivateLesson?.units) {
      products.push({
        code: selectedPrivateLesson?.code,
        units: selectedPrivateLesson?.units,
      });
    }

    return {
      options,
      products,
      quoteId,
    };
  }

  mapOnlineUserClassTimes(selectedClassTime: ClassTime, timezone: Timezone): string[] | undefined {
    if (!selectedClassTime?.startTime || !timezone) {
      return undefined;
    }

    const startTime = convertTimeWIthOffsetToString(selectedClassTime.startTime, timezone.timezoneOffset);
    const endTime = convertTimeWIthOffsetToString(selectedClassTime.endTime, timezone.timezoneOffset);

    return [`${startTime} - ${endTime} (${timezone.formattedTimezoneOffset})`];
  }

  private getAccommodationProduct(selectedAccommodation: BaseAccommodation, selectedDuration: number, extrasSummary: ExtrasSummary) {
    if (selectedAccommodation?.code) {
      return {
        code: selectedAccommodation.code,
        units: selectedDuration,
      };
    } else if (!selectedAccommodation?.code && selectedAccommodation?.type !== 'OTHER') {
      const accommodationFromExtrasSummary = extrasSummary?.accommodations.find((accommodation) => accommodation.type === selectedAccommodation?.type);
      if (accommodationFromExtrasSummary) {
        return {
          code: accommodationFromExtrasSummary.code,
          units: selectedDuration,
        };
      }
    }
    return undefined;
  }
}
